// exportUtils.js
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Export data to Excel format
 * @param {Object} options - Export configuration
 * @param {Array} options.data - Data to export
 * @param {Array} options.columns - Column definitions
 * @param {string} [options.fileName='export'] - Output filename
 * @param {string} [options.sheetName='Sheet1'] - Worksheet name
 */
export const exportToExcel = (options) => {
  try {
    const { data, columns, fileName = 'export', sheetName = 'Sheet1' } = options;
    
    if (!data || !Array.isArray(data)) {
      throw new Error('Invalid data provided for export');
    }

    if (!columns || !Array.isArray(columns)) {
      throw new Error('Invalid columns configuration');
    }

    // Prepare the data for Excel
    const excelData = data.map(item => {
      const row = {};
      columns.forEach(col => {
        try {
          // Handle nested properties (e.g., 'category.name')
          const value = col.key.split('.').reduce((obj, key) => 
            (obj && obj[key] !== undefined) ? obj[key] : null, item);
          
          // Apply formatting if specified
          row[col.header] = col.format ? col.format(value) : value;
        } catch (e) {
          console.warn(`Error processing column ${col.key}:`, e);
          row[col.header] = '';
        }
      });
      return row;
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Add worksheet to workbook and save
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

/**
 * Export data to PDF format
 * @param {Object} options - Export configuration
 * @param {Array} options.data - Data to export
 * @param {Array} options.columns - Column definitions
 * @param {string} [options.fileName='export'] - Output filename
 * @param {string} [options.title='Export'] - Document title
 * @param {string} [options.orientation='portrait'] - Page orientation
 */
export const exportToPDF = (options) => {
  try {
    const { 
      data, 
      columns, 
      fileName = 'export', 
      title = 'Export', 
      orientation = 'portrait' 
    } = options;

    if (!data || !Array.isArray(data)) {
      throw new Error('Invalid data provided for export');
    }

    if (!columns || !Array.isArray(columns)) {
      throw new Error('Invalid columns configuration');
    }

    // Create new PDF document
    const doc = new jsPDF({
      orientation,
      unit: 'mm'
    });
    
    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 10);
    
    // Prepare table data
    const headers = columns.map(col => col.header);
    const body = data.map(item => 
      columns.map(col => {
        try {
          // Handle nested properties
          const value = col.key.split('.').reduce((obj, key) => 
            (obj && obj[key] !== undefined) ? obj[key] : null, item);
          
          return col.format ? col.format(value) : (value || '');
        } catch (e) {
          console.warn(`Error processing column ${col.key}:`, e);
          return '';
        }
      })
    );
    
    // Add table
    doc.autoTable({
      head: [headers],
      body: body,
      startY: 20,
      styles: { 
        overflow: 'linebreak',
        fontSize: 9,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        // Default style
        0: { cellWidth: 'auto' }
      },
      margin: { top: 20 }
    });
    
    // Save the PDF
    doc.save(`${fileName}.pdf`);
    
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

/**
 * Helper function to flatten nested data structure
 * @param {Array} data - Original data with nested objects
 * @returns {Array} Flattened data
 */
export const flattenData = (data) => {
  if (!data) return [];
  
  return data.map(item => {
    const flatItem = {...item};
    
    // Flatten nested objects
    Object.keys(item).forEach(key => {
      if (typeof item[key] === 'object' && item[key] !== null) {
        Object.keys(item[key]).forEach(subKey => {
          flatItem[`${key}.${subKey}`] = item[key][subKey];
        });
      }
    });
    
    return flatItem;
  });
};